import React, { useState, useEffect, useRef } from 'react';
import './VideoWithFallback.css';

interface VideoWithFallbackProps {
  videoSrc: string;
  fallbackImageSrc: string;
  className?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  alt?: string;
}

const VideoWithFallback: React.FC<VideoWithFallbackProps> = ({
  videoSrc,
  fallbackImageSrc,
  className = '',
  autoPlay = true,
  muted = true,
  loop = true,
  playsInline = true,
  alt = 'Video content'
}) => {
  const [useVideo, setUseVideo] = useState<boolean | null>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const connectionRef = useRef<any>(null);

  useEffect(() => {
    const checkConnection = () => {
      const connection = (navigator as any)?.connection || (navigator as any)?.mozConnection || (navigator as any)?.webkitConnection;
      connectionRef.current = connection;

      if (connection) {
        const { effectiveType, downlink } = connection;

        // Only use fallback image for really slow connections (2G or very slow 3G)
        const shouldUseVideo = !(
          effectiveType === '2g' ||
          (effectiveType === 'slow-2g') ||
          (effectiveType === '3g' && downlink < 0.5)
        );

        setUseVideo(shouldUseVideo);
      } else {
        // Fallback: check if user prefers reduced data usage, otherwise use video
        const prefersReducedData = window.matchMedia('(prefers-reduced-data: reduce)').matches;
        setUseVideo(!prefersReducedData);
      }
    };

    // Initial check
    checkConnection();

    // Listen for connection changes
    const connection = connectionRef.current;
    if (connection && connection.addEventListener) {
      connection.addEventListener('change', checkConnection);

      return () => {
        connection.removeEventListener('change', checkConnection);
      };
    }
  }, []);

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  const handleVideoError = () => {
    setUseVideo(false);
  };

  // Show poster image while loading or for slow connections
  if (useVideo === null || !useVideo) {
    return (
      <div className="video-container">
        <img
          src={fallbackImageSrc}
          alt={alt}
          className={`${className} video-poster`}
        />
        {useVideo === null && (
          <div className="video-loading-indicator">
            <div className="loading-spinner"></div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="video-container">
      <video
        ref={videoRef}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        playsInline={playsInline}
        poster={fallbackImageSrc}
        className={`${className} ${!isVideoLoaded ? 'video-loading' : ''}`}
        onCanPlayThrough={handleVideoLoad}
        onLoadedData={handleVideoLoad}
        onError={handleVideoError}
        preload="metadata"
      >
        <source src={videoSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {!isVideoLoaded && (
        <img
          src={fallbackImageSrc}
          alt={alt}
          className={`${className} video-placeholder`}
        />
      )}
    </div>
  );
};

export default VideoWithFallback;